import {
  type AddInboxTag,
  type ClaimInboxConversation,
  type InboxChannels,
  InboxChannelsSchema,
  type InboxConversationDetail,
  InboxConversationDetailSchema,
  type InboxConversationPage,
  InboxConversationPageSchema,
  type InboxMacros,
  InboxMacrosSchema,
  type InboxMemberResponse,
  InboxMemberResponseSchema,
  type InboxNoticesResponse,
  InboxNoticesResponseSchema,
  type InboxOwnershipConflict,
  InboxOwnershipConflictSchema,
  type InboxPostPreview,
  InboxPostPreviewSchema,
  InboxReadMarkerSchema,
  type InboxTags,
  InboxTagsSchema,
  type LinkInboxIdentity,
  type ListInboxConversationsQuery,
  type SendInboxReply,
  type UpdateInboxStatus,
} from '@sbc/domain-models/gateway';
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ApiError, apiDelete, apiGet, apiGetBlob, apiSend, apiSendForm } from './client';

const INBOX_KEY = ['admin', 'inbox'];
const LIVE_INTERVAL_MS = 10_000;
const MEMBER_STALE_MS = 60_000;
const POST_STALE_MS = 30 * 60_000;
const CHANNELS_STALE_MS = 5 * 60_000;
const TAGS_STALE_MS = 60_000;
const MACROS_STALE_MS = 5 * 60_000;
const NOTICES_INTERVAL_MS = 60_000;
const MEDIA_GC_MS = 10 * 60_000;

export interface InboxMediaFile {
  url: string;
  type: string;
}

export interface InboxMediaCopy {
  file: InboxMediaFile | null;
  failed: boolean;
}

export function useInboxMediaCopy(
  conversationId: string,
  messageId: string,
  enabled: boolean,
): InboxMediaCopy {
  return useStoredCopy(
    ['media', conversationId, messageId],
    `/api/admin/inbox/${conversationId}/messages/${messageId}/media`,
    enabled,
  );
}

export function useInboxAttachmentCopy(
  conversationId: string,
  messageId: string,
  attachmentId: string,
  enabled: boolean,
): InboxMediaCopy {
  return useStoredCopy(
    ['attachment', conversationId, messageId, attachmentId],
    `/api/admin/inbox/${conversationId}/messages/${messageId}/attachments/${attachmentId}`,
    enabled,
  );
}

function useStoredCopy(key: string[], path: string, enabled: boolean): InboxMediaCopy {
  const query = useQuery<Blob>({
    queryKey: [...INBOX_KEY, ...key],
    queryFn: () => apiGetBlob(path),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: MEDIA_GC_MS,
    retry: 1,
    enabled,
  });
  const [file, setFile] = useState<InboxMediaFile | null>(null);
  useEffect(() => {
    if (!query.data) {
      setFile(null);
      return undefined;
    }
    const url = URL.createObjectURL(query.data);
    setFile({ url, type: query.data.type });
    return () => URL.revokeObjectURL(url);
  }, [query.data]);
  return { file, failed: query.isError };
}

function toSearch(query: ListInboxConversationsQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  for (const channel of query.channel ?? []) {
    params.append('channel', channel);
  }
  if (query.surface) {
    params.set('surface', query.surface);
  }
  if (query.status) {
    params.set('status', query.status);
  }
  if (query.search) {
    params.set('search', query.search);
  }
  if (query.category) {
    params.set('category', query.category);
  }
  if (query.tag) {
    params.set('tag', query.tag);
  }
  if (query.assignee) {
    params.set('assignee', query.assignee);
  }
  if (query.unread) {
    params.set('unread', 'true');
  }
  return params.toString();
}

export function ownershipConflictOf(error: unknown): InboxOwnershipConflict | null {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return null;
  }
  const parsed = InboxOwnershipConflictSchema.safeParse(error.body);
  return parsed.success ? parsed.data : null;
}

export function useInboxConversations(query: ListInboxConversationsQuery) {
  return useQuery<InboxConversationPage>({
    queryKey: [...INBOX_KEY, 'list', query],
    queryFn: () => apiGet(`/api/admin/inbox?${toSearch(query)}`, InboxConversationPageSchema),
    refetchInterval: LIVE_INTERVAL_MS,
    placeholderData: (previous) => previous,
  });
}

export function useInboxChannels() {
  return useQuery<InboxChannels>({
    queryKey: [...INBOX_KEY, 'channels'],
    queryFn: () => apiGet('/api/admin/inbox/channels', InboxChannelsSchema),
    staleTime: CHANNELS_STALE_MS,
  });
}

export function useInboxTags() {
  return useQuery<InboxTags>({
    queryKey: [...INBOX_KEY, 'tags'],
    queryFn: () => apiGet('/api/admin/inbox/tags', InboxTagsSchema),
    staleTime: TAGS_STALE_MS,
  });
}

export function useInboxMacros(enabled: boolean) {
  return useQuery<InboxMacros>({
    queryKey: [...INBOX_KEY, 'macros'],
    queryFn: () => apiGet('/api/admin/inbox/macros', InboxMacrosSchema),
    staleTime: MACROS_STALE_MS,
    enabled,
  });
}

export function useInboxNotices() {
  return useQuery<InboxNoticesResponse>({
    queryKey: [...INBOX_KEY, 'notices'],
    queryFn: () => apiGet('/api/admin/inbox/notices', InboxNoticesResponseSchema),
    refetchInterval: NOTICES_INTERVAL_MS,
  });
}

export function useInboxConversation(id: string) {
  return useQuery<InboxConversationDetail>({
    queryKey: [...INBOX_KEY, 'detail', id],
    queryFn: () => apiGet(`/api/admin/inbox/${id}`, InboxConversationDetailSchema),
    refetchInterval: LIVE_INTERVAL_MS,
  });
}

export function useInboxMember(id: string, enabled: boolean) {
  return useQuery<InboxMemberResponse>({
    queryKey: [...INBOX_KEY, 'member', id],
    queryFn: () => apiGet(`/api/admin/inbox/${id}/member`, InboxMemberResponseSchema),
    staleTime: MEMBER_STALE_MS,
    enabled,
  });
}

export function useInboxPostPreview(id: string, enabled: boolean) {
  return useQuery<InboxPostPreview>({
    queryKey: [...INBOX_KEY, 'post', id],
    queryFn: () => apiGet(`/api/admin/inbox/${id}/post`, InboxPostPreviewSchema),
    staleTime: POST_STALE_MS,
    enabled,
  });
}

function useInboxMutation<TInput>(
  mutationFn: (input: TInput) => Promise<unknown>,
  options: Pick<UseMutationOptions<unknown, Error, TInput>, 'scope' | 'onMutate'> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX_KEY });
    },
  });
}

const READ_STATE_SCOPE = { id: 'inbox-read-state' };
const LIST_KEY = [...INBOX_KEY, 'list'];

export function withConversationUnread(
  page: InboxConversationPage,
  id: string,
): InboxConversationPage {
  return {
    ...page,
    items: page.items.map((item) => (item.id === id ? { ...item, unread: true } : item)),
  };
}

export function useMarkInboxRead() {
  return useInboxMutation(
    (id: string) => apiSend('POST', `/api/admin/inbox/${id}/read`, {}, InboxReadMarkerSchema),
    {
      scope: READ_STATE_SCOPE,
    },
  );
}

export function useMarkInboxUnread() {
  const queryClient = useQueryClient();
  return useInboxMutation((id: string) => apiDelete(`/api/admin/inbox/${id}/read`), {
    scope: READ_STATE_SCOPE,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      queryClient.setQueriesData<InboxConversationPage>({ queryKey: LIST_KEY }, (page) =>
        page ? withConversationUnread(page, id) : page,
      );
    },
  });
}

export type InboxReplyInput = SendInboxReply & { file?: File };

export function useSendInboxReply(id: string) {
  return useInboxMutation(({ file, ...reply }: InboxReplyInput) =>
    file
      ? apiSendForm(
          `/api/admin/inbox/${id}/reply/attachment`,
          attachmentReplyForm(reply, file),
          InboxConversationDetailSchema,
        )
      : apiSend('POST', `/api/admin/inbox/${id}/reply`, reply, InboxConversationDetailSchema),
  );
}

export function attachmentReplyForm(reply: SendInboxReply, file: File): FormData {
  const form = new FormData();
  form.set('text', reply.text);
  if (reply.takeOver) {
    form.set('takeOver', 'true');
  }
  form.set('file', file, file.name);
  return form;
}

export function useClaimInboxConversation(id: string) {
  return useInboxMutation((input: ClaimInboxConversation) =>
    apiSend('POST', `/api/admin/inbox/${id}/claim`, input, InboxConversationDetailSchema),
  );
}

export function useReleaseInboxConversation(id: string) {
  return useInboxMutation(() =>
    apiSend('POST', `/api/admin/inbox/${id}/release`, {}, InboxConversationDetailSchema),
  );
}

export function useSetInboxMessageHidden(id: string) {
  return useInboxMutation((input: { messageId: string; hidden: boolean }) =>
    apiSend(
      'POST',
      `/api/admin/inbox/${id}/messages/${input.messageId}/${input.hidden ? 'hide' : 'unhide'}`,
      {},
      InboxConversationDetailSchema,
    ),
  );
}

export function useLinkInboxIdentity(id: string) {
  return useInboxMutation((input: LinkInboxIdentity) =>
    apiSend('POST', `/api/admin/inbox/${id}/identity`, input, InboxConversationDetailSchema),
  );
}

export function useAddInboxTag(id: string) {
  return useInboxMutation((input: AddInboxTag) =>
    apiSend('POST', `/api/admin/inbox/${id}/tags`, input, InboxConversationDetailSchema),
  );
}

export function useRemoveInboxTag(id: string) {
  return useInboxMutation((tagId: string) => apiDelete(`/api/admin/inbox/${id}/tags/${tagId}`));
}

export function useUnlinkInboxIdentity(id: string) {
  return useInboxMutation(() => apiDelete(`/api/admin/inbox/${id}/identity`));
}

export function useUpdateInboxStatus() {
  return useInboxMutation((input: UpdateInboxStatus & { id: string }) =>
    apiSend(
      'POST',
      `/api/admin/inbox/${input.id}/status`,
      { status: input.status },
      InboxConversationDetailSchema,
    ),
  );
}
